import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  AvatarGroup,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  AutoAwesome as EvolveIcon,
  Image as ImageIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as SelectIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingIcon,
  Lightbulb as IdeaIcon,
  Palette as PaletteIcon,
  Code as CodeIcon,
  School as SchoolIcon,
  Psychology as PsychologyIcon,
  Science as ScienceIcon,
  Business as BusinessIcon,
  Brush as BrushIcon,
  PlayArrow as PlayIcon,
  ModelTraining as TrainingIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Api as ApiIcon,
} from '@mui/icons-material';
import { 
  PromptEvolutionSession,
  PromptEvolutionAgent,
  PROMPT_EVOLUTION_AGENTS,
  AgentCategory,
  GeneratedPrompt,
  getAgentsByCategory,
} from '../models/promptEvolution';
import { PromptEvolutionService } from '../services/promptEvolutionService';
import { PromptEvolutionAgentService } from '../services/promptEvolutionAgentService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import ImageGenerationPanel from './ImageGenerationPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

const getCategoryIcon = (category: AgentCategory) => {
  const icons: Record<AgentCategory, React.ReactElement> = {
    creative: <BrushIcon />,
    technical: <CodeIcon />,
    visual: <PaletteIcon />,
    narrative: <IdeaIcon />,
    scientific: <ScienceIcon />,
    marketing: <BusinessIcon />,
    educational: <SchoolIcon />,
    specialized: <PsychologyIcon />,
  };
  return icons[category] || <CategoryIcon />;
};

export const PromptEvolutionStudio = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch persisted sessions and assets from backend
  const {
    sessions: backendSessions,
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'PromptImprovementSession',  // Fixed: Correct SessionType from backend
    sessionBased: true,
    autoFetch: true,
  });

  const [activeTab, setActiveTab] = useState(0);
  const [sessions, setSessions] = useState<PromptEvolutionSession[]>([]);
  const [currentSession, setCurrentSession] = useState<PromptEvolutionSession | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<AgentCategory>('creative');
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [temperature, setTemperature] = useState(0.7);
  const [imagePrompt, setImagePrompt] = useState('');
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [loraDialogOpen, setLoraDialogOpen] = useState(false);
  const [loraPromptCount, setLoraPromptCount] = useState(10);
  const [loraPromptStyle, setLoraPromptStyle] = useState<'short' | 'detailed'>('detailed');
  const [loraPrompts, setLoraPrompts] = useState<any[]>([]);
  const [loraLoading, setLoraLoading] = useState(false);
  const [expandedIterations, setExpandedIterations] = useState<Set<string>>(new Set());

  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    const loadSessions = async () => {
      // Load from localStorage
      const localSessions = PromptEvolutionAgentService.getAllSessions();

      // Merge with backend sessions
      if (backendSessions && backendSessions.length > 0) {
        try {
          console.log('[PromptEvolutionStudio] Backend sessions received:', backendSessions);

          // Fetch full session details for each backend session
          const sessionDetailsPromises = backendSessions.map(async (bs) => {
            // bs is already a SessionWithAssets object, check if it has the required structure
            if (!bs?.session?.id) {
              console.warn('[PromptEvolutionStudio] Invalid backend session structure:', bs);
              return null;
            }

            // Parse sessionData JSON string to extract prompt iterations
            let sessionData: any = {};
            let promptIterations: string[] = [];

            try {
              if (bs.session.sessionData) {
                sessionData = JSON.parse(bs.session.sessionData);
                promptIterations = sessionData.PromptIterations || [];
                console.log('[PromptEvolutionStudio] Parsed session data:', sessionData);
              }
            } catch (e) {
              console.error('[PromptEvolutionStudio] Failed to parse sessionData:', e);
            }

            // Convert PromptIterations into iteration format expected by UI
            const iterations = promptIterations.slice(1).map((prompt, index) => {
              return {
                id: `iteration-${bs.session.id}-${index}`,
                iterationNumber: index + 1,
                agentType: 'prompt-improvement',
                agentName: 'Prompt Improvement Agent',
                inputPrompt: promptIterations[index] || sessionData.StartingPrompt || '',
                generatedPrompts: [
                  {
                    id: `variant-1-${index}`,
                    prompt: prompt,
                    explanation: 'Improved prompt from backend session',
                  }
                ],
                selectedPromptIndex: 0,
                timestamp: bs.session.lastUpdatedAt || bs.session.createdAt,
              };
            });

            // Convert backend session to PromptEvolutionSession format
            return {
              id: bs.session.id,
              title: bs.session.title || sessionData.Title || `Evolution - ${new Date(bs.session.createdAt).toLocaleDateString()}`,
              description: bs.session.concept || 'Imported from backend',
              initialPrompt: sessionData.StartingPrompt || promptIterations[0] || '',
              currentPrompt: promptIterations[promptIterations.length - 1] || sessionData.StartingPrompt || '',
              iterations: iterations,
              metadata: {
                totalIterations: iterations.length,
                agentsUsed: ['prompt-improvement'],
                exportHistory: [],
                tags: [],
                backendSessionId: bs.session.id,
              },
              createdAt: bs.session.createdAt,
              updatedAt: bs.session.lastUpdatedAt || bs.session.createdAt,
            } as PromptEvolutionSession;
          });

          const convertedBackendSessions = (await Promise.all(sessionDetailsPromises))
            .filter((s): s is PromptEvolutionSession => s !== null);

          // Merge: prefer backend sessions (they're authoritative)
          const sessionMap = new Map<string, PromptEvolutionSession>();

          // Add local sessions first
          localSessions.forEach(s => sessionMap.set(s.id, s));

          // Override with backend sessions
          convertedBackendSessions.forEach(s => sessionMap.set(s.id, s));

          setSessions(Array.from(sessionMap.values()));
        } catch (error) {
          console.error('Error loading backend sessions:', error);
          // Fall back to local sessions on error
          setSessions(localSessions);
        }
      } else {
        setSessions(localSessions);
      }
    };

    loadSessions();
  }, [backendSessions]);

  useEffect(() => {
    if (currentSession) {
      setImagePrompt(currentSession.currentPrompt);
    }
  }, [currentSession]);

  const handleCreateSession = () => {
    if (!sessionTitle || !initialPrompt) return;

    const session = PromptEvolutionAgentService.createSession(
      sessionTitle,
      initialPrompt,
      sessionDescription
    );

    setCurrentSession(session);
    refetchBackendData(); // Refetch to get latest backend data
    setCreateDialogOpen(false);
    setSessionTitle('');
    setSessionDescription('');
    setInitialPrompt('');
    setActiveTab(1); // Go to evolution tab
  };

  const handleEvolvePrompt = async () => {
    if (!currentSession || !selectedAgent) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await PromptEvolutionAgentService.evolvePrompt(
        currentSession.id,
        selectedAgent,
        customInstructions,
        temperature
      );

      if (response.success) {
        // Reload session to get updated data
        const updatedSession = PromptEvolutionAgentService.getSession(currentSession.id);
        if (updatedSession) {
          setCurrentSession(updatedSession);
        }
        setCustomInstructions('');

        // Show success message with refine option
        setSuccess('Prompt evolved successfully! Select a variant above or refine further with another agent.');

        // Clear agent selection to encourage trying different agents
        setSelectedAgent('');

        // Scroll to show the new results
        setTimeout(() => {
          const historySection = document.querySelector('.MuiStepper-root');
          historySection?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      } else {
        setError(response.error || 'Failed to evolve prompt');
      }
    } catch (err: any) {
      console.error('Evolution error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.title || err.message || 'Failed to evolve prompt';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPrompt = (iterationId: string, promptIndex: number) => {
    if (!currentSession) return;

    PromptEvolutionAgentService.selectPrompt(currentSession.id, iterationId, promptIndex);
    const updatedSession = PromptEvolutionAgentService.getSession(currentSession.id);
    if (updatedSession) {
      setCurrentSession(updatedSession);
      setImagePrompt(updatedSession.currentPrompt);
    }
  };

  const handleDeleteSession = (sessionId: string) => {
    PromptEvolutionAgentService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    refetchBackendData(); // Refetch to sync with backend
  };

  const handleExport = (format: 'json' | 'markdown' | 'text' = 'markdown') => {
    if (!currentSession) return;
    PromptEvolutionAgentService.exportSession(currentSession, format);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleIterationExpanded = (iterationId: string) => {
    const newExpanded = new Set(expandedIterations);
    if (newExpanded.has(iterationId)) {
      newExpanded.delete(iterationId);
    } else {
      newExpanded.add(iterationId);
    }
    setExpandedIterations(newExpanded);
  };

  const handleGenerateLoraPrompts = async () => {
    if (!currentSession) return;

    setLoraLoading(true);
    try {
      const response = await PromptEvolutionService.generateLoraPrompts(
        currentSession.currentPrompt,
        loraPromptCount,
        loraPromptStyle
      );

      if (response.success) {
        setLoraPrompts(response.prompts);
      } else {
        setError(response.error || 'Failed to generate LORA prompts');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate LORA prompts');
    } finally {
      setLoraLoading(false);
    }
  };

  const exportLoraPrompts = () => {
    if (!loraPrompts.length) return;

    const content = loraPrompts.map((p, i) => `${i + 1}. ${p.prompt}`).join('\n\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lora-training-prompts-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getFilteredAgents = (): PromptEvolutionAgent[] => {
    return getAgentsByCategory(selectedCategory);
  };

  const renderPromptCard = (prompt: GeneratedPrompt, iterationId: string, index: number, isSelected: boolean) => {
    return (
      <Card 
        key={prompt.id} 
        sx={{ 
          height: '100%',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          bgcolor: isSelected ? 'action.selected' : 'background.paper',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
            <Chip 
              label={`Variant ${index + 1}`} 
              size="small" 
              color={isSelected ? 'primary' : 'default'}
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            />
            {isSelected && <SelectIcon color="primary" fontSize={isMobile ? 'small' : 'medium'} />}
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              whiteSpace: 'pre-wrap',
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }}
          >
            {prompt.prompt}
          </Typography>
          
          {prompt.explanation && (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              {prompt.explanation}
            </Typography>
          )}
        </CardContent>
        
        <CardActions sx={{ p: { xs: 1, sm: 2 } }}>
          <Button
            size="small"
            variant={isSelected ? 'contained' : 'outlined'}
            onClick={() => handleSelectPrompt(iterationId, index)}
            startIcon={!isMobile && <SelectIcon />}
            sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
          >
            {isSelected ? 'Selected' : 'Select'}
          </Button>
          <IconButton 
            size="small" 
            onClick={() => copyToClipboard(prompt.prompt)}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </CardActions>
      </Card>
    );
  };

  const renderAgentSelector = () => {
    const filteredAgents = getFilteredAgents();

    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
        >
          Which direction would you like to take your prompt?
        </Typography>
        
        <ToggleButtonGroup
          value={selectedCategory}
          exclusive
          onChange={(_, value) => value && setSelectedCategory(value)}
          sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}
          size={isMobile ? 'small' : 'medium'}
        >
          <ToggleButton
            value="creative"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 48 }
            }}
          >
            Creative
          </ToggleButton>
          <ToggleButton 
            value="visual"
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 48 }
            }}
          >
            Visual
          </ToggleButton>
          <ToggleButton 
            value="narrative"
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 48 }
            }}
          >
            Narrative
          </ToggleButton>
          <ToggleButton 
            value="technical"
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 48 }
            }}
          >
            Technical
          </ToggleButton>
          <ToggleButton 
            value="scientific"
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 48 }
            }}
          >
            Scientific
          </ToggleButton>
          <ToggleButton 
            value="specialized"
            sx={{ 
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 48 }
            }}
          >
            Specialized
          </ToggleButton>
        </ToggleButtonGroup>

        <Grid 
          container 
          spacing={{ xs: 1.5, sm: 2 }}
          sx={{
            width: '100%',
            margin: 0,
            '& > .MuiGrid-item': {
              paddingLeft: { xs: '6px', sm: '8px' }
            }
          }}
        >
          {filteredAgents.map(agent => (
            <Grid item xs={12} md={6} key={agent.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedAgent === agent.id ? 2 : 1,
                  borderColor: selectedAgent === agent.id ? 'primary.main' : 'divider',
                  '&:hover': { borderColor: 'primary.light' },
                }}
                onClick={() => setSelectedAgent(agent.id)}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, mb: 1 }}>
                    {getCategoryIcon(agent.category)}
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      {agent.name}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    paragraph 
                    sx={{ 
                      minHeight: { xs: '2.5em', sm: '3em' },
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                  >
                    {agent.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {agent.specialties.map((specialty, i) => (
                      <Chip 
                        key={i} 
                        label={specialty} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 }, 
        px: { xs: 1.5, sm: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
            flexWrap: 'wrap'
          }}
        >
          <EvolveIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Prompt Evolution' : 'Prompt Evolution Studio'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 0.5, sm: 0 }
          }}
        >
          {isMobile 
            ? 'Evolve and refine prompts using specialized agents'
            : 'Evolve and refine prompts using a library of specialized niche agents'}
        </Typography>
      </Box>

      <Paper 
        elevation={0}
        sx={{ 
          border: 1,
          borderColor: 'divider',
          borderRadius: { xs: 1, sm: 2 },
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <Box sx={{ 
          p: { xs: 1.5, sm: 3 },
          width: '100%',
          overflow: 'hidden'
        }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                borderRadius: 1.5,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2, 
                borderRadius: 1.5,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }} 
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}

          <Tabs 
            value={activeTab} 
            onChange={(_, val) => setActiveTab(val)}
            sx={{ 
              mb: 2,
              minHeight: { xs: 44, sm: 48 },
              '& .MuiTab-root': {
                minHeight: { xs: 44, sm: 48 },
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                px: { xs: 1, sm: 2 }
              }
            }}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            allowScrollButtonsMobile
          >
            <Tab label="Sessions" />
            <Tab 
              label="Evolution" 
              disabled={!currentSession}
            />
            <Tab 
              label={isMobile ? 'Images' : 'Image Generation'}
              disabled={!currentSession}
            />
            <Tab 
              label="Settings" 
              disabled={!currentSession}
            />
          </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              fullWidth={isMobile}
              sx={{
                py: { xs: 1.25, sm: 1.5 },
                borderRadius: 2,
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                textTransform: 'none',
                minHeight: 48
              }}
            >
              {isMobile ? 'New Session' : 'New Evolution Session'}
            </Button>
          </Box>

          <Grid 
            container 
            spacing={{ xs: 2, sm: 3 }}
            sx={{
              width: '100%',
              margin: 0,
              '& > .MuiGrid-item': {
                paddingLeft: { xs: '8px', sm: '12px' }
              }
            }}
          >
            {sessions.map(session => (
              <Grid item xs={12} sm={6} md={4} key={session.id}>
                <Card>
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                    >
                      {session.title}
                    </Typography>
                    {session.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        paragraph
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {session.description}
                      </Typography>
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ mb: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} 
                      noWrap
                    >
                      Initial: {session.initialPrompt}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        {session.metadata.totalIterations} iterations
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        •
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      >
                        {session.metadata.agentsUsed.length} agents used
                      </Typography>
                    </Box>
                    <AvatarGroup max={4} sx={{ justifyContent: 'flex-start' }}>
                      {session.metadata.agentsUsed.map((agentId, i) => {
                        const agent = PROMPT_EVOLUTION_AGENTS.find(a => a.id === agentId);
                        return (
                          <Tooltip key={i} title={agent?.name || agentId}>
                            <Avatar sx={{ width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 }, fontSize: { xs: 10, sm: 12 } }}>
                              {agent?.name.charAt(0) || '?'}
                            </Avatar>
                          </Tooltip>
                        );
                      })}
                    </AvatarGroup>
                  </CardContent>
                  <CardActions sx={{ p: { xs: 1, sm: 2 } }}>
                    <Button
                      size="small"
                      onClick={() => {
                        setCurrentSession(session);
                        setActiveTab(1);
                      }}
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      Open
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteSession(session.id)}
                      color="error"
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {currentSession && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {/* Current Prompt Section */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  Current Prompt
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  value={currentSession.currentPrompt}
                  InputProps={{ 
                    readOnly: true,
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    startIcon={!isMobile && <CopyIcon />}
                    onClick={() => copyToClipboard(currentSession.currentPrompt)}
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    Copy
                  </Button>
                  <Button
                    size="small"
                    startIcon={!isMobile && <ImageIcon />}
                    onClick={() => setActiveTab(2)}
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? 'Image' : 'Generate Image'}
                  </Button>
                  {currentSession.iterations.length > 0 && (
                    <>
                      <Button
                        size="small"
                        startIcon={!isMobile && <RefreshIcon />}
                        onClick={() => {
                          setSelectedAgent('');
                          // Focus on agent selector
                          setTimeout(() => {
                            const agentSection = document.getElementById('agent-selector');
                            agentSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 100);
                        }}
                        color="primary"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {isMobile ? 'Refine' : 'Refine Further'}
                      </Button>
                      <Button
                        size="small"
                        startIcon={!isMobile && <TrainingIcon />}
                        onClick={() => {
                          setLoraDialogOpen(true);
                          setLoraPrompts([]);
                        }}
                        color="secondary"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {isMobile ? 'LORA' : 'Generate LORA Training Set'}
                      </Button>
                    </>
                  )}
                </Box>
              </Paper>

              {/* Evolve Prompt Section - Full Width */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }} id="agent-selector">
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  Evolve Prompt
                </Typography>
                
                {/* Show refinement suggestion */}
                {currentSession.iterations.length > 0 && !selectedAgent && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      Select a different agent to refine your prompt further
                    </Typography>
                  </Alert>
                )}

                {renderAgentSelector()}

                <Box sx={{ 
                  mt: { xs: 2, sm: 3 }, 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2 
                }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    label="Custom Instructions (optional)"
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="Add specific guidance for the agent..."
                    InputProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                  
                  <Box sx={{ minWidth: { xs: '100%', md: 200 } }}>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Temperature: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, val) => setTemperature(val as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks
                      sx={{ mb: 2 }}
                    />
                    
                    <Button
                      variant="contained"
                      fullWidth
                      size={isMobile ? 'medium' : 'large'}
                      startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                      onClick={handleEvolvePrompt}
                      disabled={!selectedAgent || loading}
                      sx={{
                        py: { xs: 1, sm: 1.5 },
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        textTransform: 'none',
                        minHeight: 44
                      }}
                    >
                      {loading ? 'Evolving...' : 'Evolve Prompt'}
                    </Button>
                  </Box>
                </Box>
              </Paper>

              {/* Evolution History Section */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  Evolution History
                </Typography>
                
                <Stepper orientation="vertical" activeStep={currentSession.iterations.length}>
                  <Step expanded>
                    <StepLabel>Initial Prompt</StepLabel>
                    <StepContent>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          p: { xs: 1.5, sm: 2 }, 
                          bgcolor: 'background.default', 
                          borderRadius: 1,
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                        }}
                      >
                        {currentSession.initialPrompt}
                      </Typography>
                    </StepContent>
                  </Step>

                  {currentSession.iterations.map((iteration, index) => {
                    const isExpanded = expandedIterations.has(iteration.id);
                    const agent = PROMPT_EVOLUTION_AGENTS.find(a => a.id === iteration.agentType);
                    
                    return (
                      <Step key={iteration.id} expanded>
                        <StepLabel
                          onClick={() => toggleIterationExpanded(iteration.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                              {agent && getCategoryIcon(agent.category)}
                              <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                {isMobile ? `#${index + 1} - ${iteration.agentName}` : `Iteration ${index + 1} - ${iteration.agentName}`}
                              </Typography>
                            </Box>
                            {iteration.customInstructions && (
                              <Chip 
                                label={isMobile ? 'Custom' : 'Custom Instructions'}
                                size="small" 
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                            )}
                            <IconButton size="small" sx={{ ml: 'auto', minWidth: 44, minHeight: 44 }}>
                              {isExpanded ? <ExpandLessIcon fontSize={isMobile ? 'small' : 'medium'} /> : <ExpandMoreIcon fontSize={isMobile ? 'small' : 'medium'} />}
                            </IconButton>
                          </Box>
                        </StepLabel>
                        <StepContent>
                          {/* Agent Info Panel - Collapsible */}
                          {isExpanded && iteration.agentResponse && (
                            <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, bgcolor: 'background.default' }}>
                              <Typography 
                                variant="subtitle2" 
                                gutterBottom 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1,
                                  fontSize: { xs: '0.875rem', sm: '1rem' }
                                }}
                              >
                                <ApiIcon fontSize="small" />
                                Agent Response Details
                              </Typography>
                              
                              <Grid 
                                container 
                                spacing={1} 
                                sx={{ 
                                  mb: 2,
                                  width: '100%',
                                  margin: 0,
                                  '& > .MuiGrid-item': {
                                    paddingLeft: '4px'
                                  }
                                }}
                              >
                                {iteration.agentResponse.agentType && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      Agent Type
                                    </Typography>
                                    <Typography 
                                      variant="body2"
                                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                    >
                                      {iteration.agentResponse.agentType}
                                    </Typography>
                                  </Grid>
                                )}
                                {iteration.agentResponse.focusArea && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      Focus Area
                                    </Typography>
                                    <Typography 
                                      variant="body2"
                                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                    >
                                      {iteration.agentResponse.focusArea}
                                    </Typography>
                                  </Grid>
                                )}
                                {iteration.agentResponse.success !== undefined && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      Status
                                    </Typography>
                                    <Chip 
                                      label={iteration.agentResponse.success ? 'Success' : 'Failed'}
                                      size="small"
                                      color={iteration.agentResponse.success ? 'success' : 'error'}
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                  </Grid>
                                )}
                                {iteration.agentResponse.iterationNumber && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    >
                                      API Iteration
                                    </Typography>
                                    <Typography 
                                      variant="body2"
                                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                                    >
                                      {iteration.agentResponse.iterationNumber}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>

                              {/* Show custom instructions if any */}
                              {iteration.customInstructions && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                  >
                                    Custom Instructions
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      bgcolor: 'action.hover', 
                                      p: { xs: 0.75, sm: 1 }, 
                                      borderRadius: 1,
                                      fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                                    }}
                                  >
                                    {iteration.customInstructions}
                                  </Typography>
                                </Box>
                              )}

                              {/* Show raw response in expandable section */}
                              <details style={{ marginTop: 8 }}>
                                <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
                                  <Typography 
                                    variant="caption" 
                                    component="span"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                  >
                                    View Raw Response
                                  </Typography>
                                </summary>
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, overflow: 'auto' }}>
                                  <pre style={{ margin: 0, fontSize: isMobile ? '0.625rem' : '0.75rem', whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(iteration.agentResponse, null, 2)}
                                  </pre>
                                </Box>
                              </details>
                            </Paper>
                          )}
                          
                          <Grid 
                            container 
                            spacing={{ xs: 1.5, sm: 2 }}
                            sx={{
                              width: '100%',
                              margin: 0,
                              '& > .MuiGrid-item': {
                                paddingLeft: { xs: '6px', sm: '8px' }
                              }
                            }}
                          >
                            {iteration.generatedPrompts.map((prompt, i) => (
                              <Grid item xs={12} key={prompt.id}>
                                {renderPromptCard(
                                  prompt,
                                  iteration.id,
                                  i,
                                  iteration.selectedPromptIndex === i
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </StepContent>
                      </Step>
                    );
                  })}
                </Stepper>
                
                {/* Refine Further Button */}
                {currentSession.iterations.length > 0 && (
                  <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      size={isMobile ? 'medium' : 'large'}
                      startIcon={!isMobile && <RefreshIcon />}
                      onClick={() => {
                        // Clear agent selection to force user to pick a new agent for refinement
                        setSelectedAgent('');
                        // Scroll to agent selector
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      sx={{ 
                        borderWidth: 2,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: 44,
                        '&:hover': {
                          borderWidth: 2,
                        }
                      }}
                    >
                      {isMobile ? 'Refine Further' : 'Refine Further with Another Agent'}
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {currentSession && (
            <Box>
              <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  Image Generation
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                >
                  Generate images using your evolved prompts
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  label="Prompt for Image Generation"
                  value={imagePrompt}
                  onChange={(e) => setImagePrompt(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
              </Paper>

              <ImageGenerationPanel
                initialPrompt={imagePrompt}
                onPromptChange={setImagePrompt}
              />
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {currentSession && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Session Settings
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Export Options
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport('markdown')}
                  >
                    Export as Markdown
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport('json')}
                  >
                    Export as JSON
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport('text')}
                  >
                    Export as Text
                  </Button>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Session Statistics
                </Typography>
                <Grid 
                  container 
                  spacing={2}
                  sx={{
                    width: '100%',
                    margin: 0,
                    '& > .MuiGrid-item': {
                      paddingLeft: '8px'
                    }
                  }}
                >
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Iterations
                    </Typography>
                    <Typography variant="h4">
                      {currentSession.metadata.totalIterations}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Agents Used
                    </Typography>
                    <Typography variant="h4">
                      {currentSession.metadata.agentsUsed.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Prompts Generated
                    </Typography>
                    <Typography variant="h4">
                      {currentSession.iterations.reduce((sum, iter) => sum + iter.generatedPrompts.length, 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Images Created
                    </Typography>
                    <Typography variant="h4">
                      {currentSession.metadata.generatedImages?.length || 0}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          )}
        </TabPanel>

        {/* Create Session Dialog */}
        <Dialog 
          open={createDialogOpen} 
          onClose={() => setCreateDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 2 },
              border: 1,
              borderColor: 'divider',
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1, p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 1,
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              <AddIcon fontSize={isMobile ? 'medium' : 'large'} />
              {isMobile ? 'New Session' : 'Create New Evolution Session'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, mt: 2 }}>
              <TextField
                fullWidth
                label="Session Title"
                placeholder="e.g., Character Design Evolution"
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                required
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 1 : 2}
                label="Description (optional)"
                placeholder="Brief description of your evolution goal..."
                value={sessionDescription}
                onChange={(e) => setSessionDescription(e.target.value)}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label="Initial Prompt"
                placeholder="Enter your starting prompt to evolve..."
                value={initialPrompt}
                onChange={(e) => setInitialPrompt(e.target.value)}
                required
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
            <Button 
              onClick={() => setCreateDialogOpen(false)}
              sx={{ 
                borderRadius: 1.5,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSession}
              disabled={!sessionTitle || !initialPrompt}
              sx={{
                borderRadius: 1.5,
                px: 3,
                fontWeight: 600,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                textTransform: 'none',
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Create Session
            </Button>
          </DialogActions>
        </Dialog>

        {/* LORA Training Prompts Dialog */}
        <Dialog 
          open={loraDialogOpen} 
          onClose={() => setLoraDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 2 }
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: { xs: 2, sm: 3 },
            fontSize: { xs: '1.125rem', sm: '1.25rem' }
          }}>
            <TrainingIcon fontSize={isMobile ? 'medium' : 'large'} /> 
            {isMobile ? 'LORA Training' : 'Generate LORA Training Prompts'}
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            {!loraPrompts.length ? (
              <>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  paragraph
                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                >
                  Generate contextually and thematically consistent variations of your evolved prompt for LORA training.
                </Typography>
                
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, bgcolor: 'background.default' }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Base Prompt:
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    {currentSession?.currentPrompt}
                  </Typography>
                </Paper>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Prompt Style
                      </InputLabel>
                      <Select
                        value={loraPromptStyle}
                        onChange={(e) => setLoraPromptStyle(e.target.value as 'short' | 'detailed')}
                        label="Prompt Style"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        <MenuItem value="short" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Short (Key elements only)
                        </MenuItem>
                        <MenuItem value="detailed" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Detailed (Full variations)
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Number of Prompts"
                      value={loraPromptCount}
                      onChange={(e) => setLoraPromptCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
                      inputProps={{ min: 1, max: 50 }}
                      InputProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                      InputLabelProps={{
                        sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size={isMobile ? 'medium' : 'large'}
                    startIcon={loraLoading ? <CircularProgress size={20} /> : (!isMobile && <TrainingIcon />)}
                    onClick={handleGenerateLoraPrompts}
                    disabled={loraLoading}
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      minHeight: 44,
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    {loraLoading ? 'Generating...' : (isMobile ? 'Generate' : 'Generate Training Prompts')}
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'center' }, 
                  mb: 2,
                  gap: 1
                }}>
                  <Typography 
                    variant="subtitle1"
                    sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    Generated {loraPrompts.length} Training Prompts
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={!isMobile && <DownloadIcon />}
                      onClick={exportLoraPrompts}
                      sx={{ 
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        flex: { xs: 1, sm: 'none' }
                      }}
                    >
                      {isMobile ? 'Export' : 'Export All'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setLoraPrompts([])}
                      sx={{ 
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        flex: { xs: 1, sm: 'none' }
                      }}
                    >
                      {isMobile ? 'New' : 'Generate New'}
                    </Button>
                  </Box>
                </Box>

                <List sx={{ maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
                  {loraPrompts.map((prompt, index) => (
                    <React.Fragment key={prompt.id}>
                      <ListItem sx={{ px: { xs: 1, sm: 2 } }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="subtitle2"
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                              >
                                #{index + 1}
                              </Typography>
                              <Chip 
                                label={prompt.category} 
                                size="small" 
                                variant="outlined"
                                color="primary"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mt: 1, 
                                whiteSpace: 'pre-wrap',
                                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                              }}
                            >
                              {prompt.prompt}
                            </Typography>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            onClick={() => copyToClipboard(prompt.prompt)}
                            size="small"
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < loraPrompts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button 
              onClick={() => setLoraDialogOpen(false)}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minHeight: 44,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Paper>
    </Container>
  );
};